import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import './DailyVisitorStats.css';

const DailyVisitorStats = () => {
  const [visitorData, setVisitorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVisitorData();
  }, []);

  const fetchVisitorData = async () => {
    try {
      setLoading(true);
      const visitorDocRef = doc(db, 'analytics', 'daily_visitors');
      const visitorDoc = await getDoc(visitorDocRef);

      if (visitorDoc.exists()) {
        const data = visitorDoc.data();
        setVisitorData(data);
      } else {
        setVisitorData({});
      }
      setError(null);
    } catch (err) {
      setError('Error fetching visitor data: ' + err.message);
      console.error('Error fetching visitor data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTodayVisitors = () => {
    if (!visitorData) return 0;
    const today = new Date().toISOString().split('T')[0];
    return visitorData[today] || 0;
  };

  const getYesterdayVisitors = () => {
    if (!visitorData) return 0;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    return visitorData[yesterdayStr] || 0;
  };

  const getTotalVisitors = () => {
    if (!visitorData) return 0;

    return Object.keys(visitorData)
      .filter((key) => /^\d{4}-\d{2}-\d{2}$/.test(key))
      .reduce((total, date) => {
        const count = Number(visitorData[date]);
        return Number.isFinite(count) ? total + count : total;
      }, 0);
  };

  const getLastSevenDays = () => {
    if (!visitorData) return [];
    
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      last7Days.push({
        date: dateStr,
        dayName,
        visitors: visitorData[dateStr] || 0
      });
    }
    return last7Days;
  };

  const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');

  if (loading) {
    return <div className="visitor-stats-loading">Loading visitor statistics...</div>;
  }

  if (error) {
    return <div className="visitor-stats-error">{error}</div>;
  }

  const lastSevenDays = getLastSevenDays();
  const maxVisitors = Math.max(1, ...lastSevenDays.map((day) => day.visitors || 0));

  return (
    <div className="daily-visitor-stats">
      <h2>📊 Daily Visitor Analytics</h2>
      
      <div className="stats-summary">
        <div className="stat-card">
          <h3>Today</h3>
          <div className="stat-number visitor-stat-number">{formatNumber(getTodayVisitors())}</div>
          <span className="stat-label">Visitors</span>
        </div>
        
        <div className="stat-card">
          <h3>Yesterday</h3>
          <div className="stat-number visitor-stat-number">{formatNumber(getYesterdayVisitors())}</div>
          <span className="stat-label">Visitors</span>
        </div>
        
        <div className="stat-card">
          <h3>Total</h3>
          <div className="stat-number visitor-stat-number">{formatNumber(getTotalVisitors())}</div>
          <span className="stat-label">All Time</span>
        </div>
      </div>

      <div className="weekly-chart">
        <h3>Last 7 Days</h3>
        <div className="chart-container">
          {lastSevenDays.map((day) => (
            <div key={day.date} className="chart-bar">
              <div 
                className="bar"
                style={{
                  height: `${Math.max(20, (day.visitors / maxVisitors) * 100)}px`
                }}
              ></div>
              <div className="bar-label">
                <div className="day-name">{day.dayName}</div>
                <div className="visitor-count">{formatNumber(day.visitors)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="refresh-section">
        <button onClick={fetchVisitorData} className="refresh-btn">
          🔄 Refresh Data
        </button>
        {visitorData?.lastUpdated && (
          <p className="last-updated">
            Last updated: {new Date(visitorData.lastUpdated).toLocaleString()}
          </p>
        )}
      </div>

      <div className="raw-data-section">
        <details>
          <summary>📋 Raw Data (Click to expand)</summary>
          <pre className="raw-data">
            {JSON.stringify(visitorData, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
};

export default DailyVisitorStats;